<template>
  <div>
    <vue-loading :active="isLoading" :is-full-page="true" />

    <BlockContainer header-variant="secondary" variant="full" v-if="isSchoolMode" :title="$t('customerCard.subTitle.info')">
      <p><strong>{{ $t('customerCard.label.holder') }}:</strong> {{ fullName(wondCustomer) }}</p>
    </BlockContainer>

    <div id="desktop-transaction-history" class="d-none d-md-block">
      <div class="transaction-history-desktop-stack">
        <BlockContainer variant="full" :title="$t('transactionHistory.subTitle.filter')" role="region">
          <DateSearchTable :callback="getTransactions" ref="dateSearchTableDesktop" />
          <template #actions>
            <Button variant="primary" id="searchBtn" type="submit" @click.stop="onSearch">{{ $t('transactionHistory.button.search') }}</Button>
          </template>
        </BlockContainer>

        <BlockContainer variant="full" :title="$t('transactionHistory.subTitle.overview')" ref="transactionOverview" role="region">
          <Table
            small
            :items="recentTransactions"
            :fields="fieldsWithActions"
            :responsive="true"
            :striped="true"
            :hover="true"
            @sort-changed="onSortChanged"
            @row-clicked="showTransactionDetails"
            :tbody-tr-attr="rowIdentifier"
            :actions="tableActions"
            @details="openTransactionDetails"
            :show-empty="true"
            :aria-label="$t('transactionHistory.label.transactionTable')"
          >
            <template #custom-foot v-if="config.TRANSACTION_HISTORY.showTotal && recentTransactions && recentTransactions.length > 0">
              <tr>
                <td class="text-left" colspan="4"><b>{{ $t('transactionHistory.tableColumn.total') }}</b></td>
                <td class="text-right" colspan="1"><b>{{ transactionsFormattedPrepaymentTotal }}</b></td>
                <td class="text-right" colspan="1"><b>{{ transactionsFormattedPaymentTotalWithoutPrepayment }}</b></td>
                <td />
              </tr>
            </template>
          </Table>

          <div v-if="config.TRANSACTION_HISTORY.showPaymentDetails">
            <Table
              small
              :items="paymentsListTotal"
              :fields="paymentFields"
              :responsive="true"
              :striped="true"
              :hover="true"
              :show-empty="true"
              :aria-label="$t('transactionHistory.label.paymentDetails')"
            >
            </Table>
          </div>
        </BlockContainer>
      </div>
    </div>

    <div id="mobile-transaction-history" class="d-md-none">
      <div class="transaction-history-mobile-stack">
        <div class="mobile-filter-container">
          <DateSearchTable :callback="getTransactions" ref="dateSearchTableMobile" />
          <Button variant="primary" id="searchBtnMobile" type="submit" class="mobile-search-btn" @click.stop="onSearch">
            {{ $t('transactionHistory.button.search') }}
          </Button>
        </div>
        <h3 class="normalText sr-only">{{ $t('transactionHistory.label.transactionTable') }}</h3>
        <CardList
          :items="recentTransactions"
          :list-aria-label="$t('transactionHistory.label.transactionTable')"
          :empty-text="$t('system.label.noData')"
          :item-aria-label="mobileItemAriaLabel"
          :item-key="mobileItemKey"
          @item-click="openTransactionDetails"
        >
          <template #item="{ item, ariaLabel, onClick }">
            <CardListItem :aria-label="ariaLabel" @click="onClick()">
              <BlockContainer :rows="mobileCardFields(item)" />
            </CardListItem>
          </template>
        </CardList>

        <Table
          v-if="config.TRANSACTION_HISTORY.showTotal && recentTransactions && recentTransactions.length > 0"
          small
          :items="mobileTotalsItems"
          :fields="mobileTotalsFields"
          :responsive="true"
          :striped="true"
          :hover="true"
          :show-empty="false"
          :aria-label="$t('transactionHistory.tableColumn.total')"
        />

        <Table
          v-if="config.TRANSACTION_HISTORY.showPaymentDetails && recentTransactions && recentTransactions.length > 0"
          small
          :items="paymentsListTotal"
          :fields="paymentFields"
          :responsive="true"
          :striped="true"
          :hover="true"
          :show-empty="false"
          :aria-label="$t('transactionHistory.label.paymentDetails')"
        />
      </div>
    </div>

    <!-- Modal for transaction details -->
    <b-modal v-if="currentTransactionSelected" id="transaction-details-modal" ref="transactionDetailsModal" @shown="focusCloseButton" :title="$t('transactionDetails.title') + ' ' + currentTransactionSelected.formattedDate" hide-footer>
      <TransactionDetails :transaction="currentTransactionSelected" />
    </b-modal>
  </div>
</template>

<script>
import TransactionDetails from "@/components/sub-components/TransactionDetails.vue";
import DateSearchTable from "@/components/main-components/DateSearchTable.vue";
import Utils from "@/utils/utils";
import VueLoading from "vue-loading-overlay";
import {accountService} from "@/services/api/accountService";
import {transactionService} from "@/services/api/transactionService";
import {useAppStore} from "@/stores/app";
import {Workflow} from "@/constants/constants";
import Table from '@/components/data-display/Table.vue';
import BlockContainer from "@/components/layout/BlockContainer.vue";
import Button from "@/components/actions/Button.vue";
import CardList from "@/components/data-display/CardList.vue";
import CardListItem from "@/components/data-display/CardListItem.vue";

export default {
  components: {
    Button,
    BlockContainer,
    VueLoading,
    TransactionDetails,
    DateSearchTable,
    Table,
    CardList,
    CardListItem,
  },
  computed: {
    config() {
      return window.config;
    },
    isSchoolMode() {
      return this.appStore.isWorkflow(Workflow.SCHOOL);
    },
    fieldsWithActions() {
      return [
        { key: 'transDate', label: this.$t('transactionHistory.tableColumn.date'), sortable: true, formatter: (value, key, item) => item.formattedDate },
        { key: 'transNum', label: this.$t('transactionHistory.tableColumn.transId'), sortable: true},
        { key: 'shopDescription', label: this.$t('transactionHistory.tableColumn.shop'), sortable: true, tdClass: 'text-left' },
        { key: 'tillDescription', label: this.$t('transactionHistory.tableColumn.till'), sortable: true },
        { key: 'formattedPrepaymentTotal', label: this.$t('transactionHistory.tableColumn.prepayment'), sortable: true, class: 'text-right', formatter: (value, key, item) => item.prepaymentTotal > 0 ? item.formattedPrepaymentTotal : '' },
        { key: 'formattedPaymentTotalWithoutPrepayment', label: this.$t('transactionHistory.tableColumn.payment'), sortable: true, class: 'text-right',
          formatter: (value, key, item) => item.paymentTotalWithoutPrepayment > 0 ? item.formattedPaymentTotalWithoutPrepayment : item.paymentsList.length === 0 ? item.formattedPaymentTotalWithoutPrepayment : '' },
        { key: 'actions', label: this.$t('transactionHistory.tableColumn.actions'), tdClass: 'text-right' }
      ];
    },
    tableActions() {
      const _self = this;
      return [
        {
          icon: ['fas', 'circle-info'],
          tooltip: function(){return _self.$t('transactionHistory.tooltip.details');},
          ariaLabel: function(item){return _self.$t('transactionHistory.label.transactionFrom',[item.formattedDate]);},
          event: 'details',
          show: () => true,
          class: 'order-details-button'
        }
      ];
    },
    paymentFields() {
      return [
        { key: 'paymentDescription', label: this.$t('transactionHistory.tableColumn.paymentDescription'), class: 'text-left', colspan: 5 },
        { key: 'formattedAmount', label: this.$t('transactionHistory.tableColumn.amount'), class: 'text-right', colspan: 2 }
      ];
    },
    mobileTotalsFields() {
      return [
        { key: 'type', label: this.$t('transactionHistory.tableColumn.type'), class: 'text-left' },
        { key: 'total', label: this.$t('transactionHistory.tableColumn.total'), class: 'text-right' },
      ];
    },
    mobileTotalsItems() {
      return [
        {
          type: this.$t('transactionHistory.tableColumn.prepayment'),
          total: this.transactionsFormattedPrepaymentTotal,
        },
        {
          type: this.$t('transactionHistory.tableColumn.payment'),
          total: this.transactionsFormattedPaymentTotalWithoutPrepayment,
        },
      ];
    },
  },
  props: {
    customerId: {
      type: [String, Number],
    }
  },
  data() {
    return {
      isLoading: false,
      wondCustomer: {},
      recentTransactions: [],
      transactionsFormattedPrepaymentTotal: 0.00,
      transactionsFormattedPaymentTotalWithoutPrepayment: 0.00,
      paymentsListTotal: [],
      currentTransactionSelected: {},
    };
  },
  setup() {
    const appStore = useAppStore();

    return {
      appStore
    };
  },
  methods: {
    focusCloseButton() {
      this.$nextTick(() => {
        const closeButton = document.querySelector("#transaction-details-modal .close");
        if (closeButton) {
          closeButton.focus();
        }
      });
    },
    fullName(customer) {
      const nameParts = [];

      if (typeof customer === 'undefined')
        return '';

      if (customer.description) {
        nameParts.push(customer.description);
      }

      if (customer.firstName) {
        nameParts.push(customer.firstName);
      }

      return nameParts.join(' ').trim();
    },
    async selectChild(id) {
      this.isLoading = true;
      const result = await accountService.selectChild(id);
      this.isLoading = false;

      if (result.exception !== undefined || result.errorCode < 0) {
        this.makeToast('danger', this.$t('transactionHistory.messages.error.onSelectChild'));
        return;
      }

      return result.wondChild;
    },
    async getTransactions(dateFrom, dateTo) {
      this.isLoading = true;
      const result = await transactionService.getTransactions(dateFrom, dateTo);
      this.isLoading = false;

      const f = new Intl.DateTimeFormat(undefined ,{localeMatcher:"best fit",dateStyle: 'medium',timeStyle: 'short'});

      this.recentTransactions = result.transactions.map(transaction => {
        const transactionDate = f.format(new Date(transaction.transDate));

        return {
          ...transaction,
          formattedDate: transactionDate,
          formattedPrepaymentAndPaymentWithoutPrepayment: `${ transaction.formattedPrepaymentTotal } / ${ transaction.formattedPaymentTotalWithoutPrepayment }`,
        };

      });

      this.transactionsFormattedPrepaymentTotal = result.transactionsFormattedPrepaymentTotal;
      this.transactionsFormattedPaymentTotalWithoutPrepayment = result.transactionsFormattedPaymentTotalWithoutPrepayment;

      this.paymentsListTotal = result.paymentsListTotal;
    },
    openTransactionDetails(item) {
      this.currentTransactionSelected = item;
      this.$nextTick(() => {
        if (this.$refs.transactionDetailsModal) {
          this.$refs.transactionDetailsModal.show();
        } else {
          console.error('Modal reference is not defined.');
        }
      });
    },
    showTransactionDetails(item) {
      if (window.config.TRANSACTION_HISTORY.showTransactionDetailsOnRowClick) {
        this.openTransactionDetails(item);
      } else {
        console.log('Modal not opened: config.showTransactionDetailsOnRowClick is false or undefined.');
      }
    },
    onSortChanged({ sortBy, sortDesc }) {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
    },
    rowIdentifier(item) {
      // if (type != 'row') {
      //   return {};
      // }
      return {
        'title': this.$t('transactionHistory.label.transactionFrom', [item.formattedDate]),
      }
    },
    mobileCardFields(item) {
      return [
        { key: 'date', label: this.$t('transactionHistory.tableColumn.date'), value: item.formattedDate },
        { key: 'shop', label: this.$t('transactionHistory.tableColumn.shop'), value: item.shopDescription },
        {
          key: 'amount',
          label: this.$t('transactionHistory.tableColumn.prepayment') + ' / ' + this.$t('transactionHistory.tableColumn.payment'),
          value: item.formattedPrepaymentAndPaymentWithoutPrepayment,
        },
      ];
    },
    mobileItemAriaLabel(item) {
      return this.$t('transactionHistory.label.transactionFrom', [item.formattedDate]);
    },
    mobileItemKey(item, index) {
      return item.guid || item.transNum || index;
    },
    onSearch() {
      const isMobile = window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches;
      const dateSearchRef = isMobile ? this.$refs.dateSearchTableMobile : this.$refs.dateSearchTableDesktop;
      if (dateSearchRef) {
        dateSearchRef.search();
      }
      // Scroll to the current transaction overview after search
      this.$nextTick(() => {
        const overviewRef = isMobile ? this.$refs.transactionOverviewMobile : this.$refs.transactionOverview;
        if (overviewRef && overviewRef.$el) {
          overviewRef.$el.scrollIntoView({ behavior: 'smooth' });
        }
      });
    },
  },
  async mounted() {
    if (this.isSchoolMode) {
      this.wondCustomer = await this.selectChild(this.customerId);
    }
    await this.getTransactions(Utils.getStartOfMonth(), Utils.getEndOfMonth());
  }
};
</script>
<style scoped>
.transaction-history-desktop-stack,
.transaction-history-mobile-stack {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
</style>
